import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { CommonActions } from '@react-navigation/native';
import { View } from 'react-native';
import emoji from 'node-emoji';
import { rem } from 'design-system/values';
import { AppView } from 'design-system/AppView';
import Indicator from 'design-system/Indicator';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import Alert from 'utils/packages/Alert';
import TextInput from 'utils/packages/TextInput';
import createStyleSheets from 'utils/createStyleSheets';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { isWeb } from '../../../constants';
import { editSubscription, getSubscriptionsById } from '../actions';
import { makeSelectSubscriptionById } from '../selectors';
const getTransactionName = (txn) => txn.customName ||
    txn.merchant?.displayName ||
    ('merchantInfo' in txn ? txn.merchantInfo?.displayName ?? '' : '') ||
    ('counterpartName' in txn ? txn.counterpartName : '') ||
    '';
const RenameSubscriptionScreen = ({ navigation, route, }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const { id, createTransaction, onSuccess } = route.params;
    const isCreate = !!createTransaction;
    useLayoutEffect(() => {
        navigation.setOptions({
            title: isCreate ? '' : 'Rename',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, isCreate]);
    useEffect(() => {
        if (id) {
            dispatch(getSubscriptionsById(id));
        }
    }, [dispatch, id]);
    const selectSubscriptionById = useMemo(makeSelectSubscriptionById, []);
    const subscription = useAppSelector((store) => selectSubscriptionById(store, id));
    if (id && !subscription) {
        return (<AppView type="primary" isInTab={isWeb} avoidHeader>
        <StatusBar barStyle="dark-content" animated/>
        <Indicator brand/>
      </AppView>);
    }
    return (<RenameSubscriptionContent navigation={navigation} subscription={subscription} createTransaction={createTransaction} onSuccess={onSuccess}/>);
};
const RenameSubscriptionContent = ({ navigation, subscription, createTransaction, onSuccess, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const isCreate = !!createTransaction;
    const initialName = useMemo(() => createTransaction
        ? getTransactionName(createTransaction)
        : subscription?.customName ||
            subscription?.merchantInfo?.displayName ||
            '', [createTransaction, subscription]);
    const [name, setName] = useState(initialName);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const onPressSubmit = async () => {
        if (!name || !name.length) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must enter a merchant.');
            return;
        }
        if (createTransaction) {
            const trimmed = name.trim();
            const newCustomName = trimmed && trimmed !== initialName.trim() ? trimmed : undefined;
            navigation.push('AdjustSubscription', {
                data: createTransaction,
                onSuccess,
                customName: newCustomName,
            });
            return;
        }
        if (!subscription) {
            return;
        }
        if (isWeb) {
            await fetch(editSubscription(subscription.id, { customName: name }));
        }
        else {
            fetch(editSubscription(subscription.id, { customName: name }));
        }
        if (navigation.canGoBack()) {
            navigation.goBack();
        }
        else {
            navigation.dispatch(CommonActions.reset({
                index: 0,
                routes: subscription
                    ? [{ name: 'Subscription', params: { id: subscription.id } }]
                    : [{ name: 'Feed' }],
            }));
        }
    };
    const textInputRef = useRef(null);
    useAutoFocusInput(textInputRef);
    return (<AppView type="primary" isInTab={isWeb} avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      {isCreate ? (<ScreenInfoHeader style={styles.header} title="Name recurring payment"/>) : null}
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
        <View style={styles.inputContainer}>
          <TextInput ref={textInputRef} autoCorrect={false} allowFontScaling={false} value={name} onChangeText={setName} placeholderTextColor={colors.text.placeholder} underlineColorAndroid="transparent" selectionColor={colors.text.selection} style={styles.textinput} placeholder="Choose Name" returnKeyType="done" onSubmitEditing={onPressSubmit}/>
        </View>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand title={isCreate ? 'Continue' : 'Save'} onPress={onPressSubmit} isFetching={isFetching} withFrame={false}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    header: {
        paddingTop: rem(8),
    },
    inputContainer: {
        flex: 1,
        margin: rem(16),
        justifyContent: 'center',
    },
    textinput: {
        fontSize: rem(32),
        fontFamily: 'Montserrat-Light',
        textAlign: 'center',
        color: colors.text.primary,
    },
}));
export default RenameSubscriptionScreen;
